import { AlertTriangle, Inbox, Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { ApiError } from "@/api/problem";
import { useI18n } from "@/i18n/provider";
import { cn } from "@/lib/utils";
import { Button, Skeleton } from "./primitives";

/**
 * Loading, empty and error. Every screen in this product has all three, because every
 * screen can be in all three states, and a screen that only handles the happy path is a
 * screen that lies to the operator when the network is bad.
 */

export function LoadingRows({ rows = 5, className }: { rows?: number; className?: string }) {
  const { t } = useI18n();
  return (
    <div className={cn("flex flex-col gap-2 p-4", className)} aria-busy="true">
      <span className="sr-only">{t("common.loading")}</span>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

export function LoadingBlock({ className }: { className?: string }) {
  const { t } = useI18n();
  return (
    <div
      className={cn("flex items-center justify-center gap-2 p-10 text-sm text-muted", className)}
      aria-busy="true"
    >
      <Loader2 className="size-4 animate-spin" aria-hidden />
      {t("common.loading")}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
  icon,
  className,
}: {
  title: string;
  body?: string;
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 px-6 py-14 text-center",
        className,
      )}
    >
      <div className="text-mauve" aria-hidden>
        {icon ?? <Inbox className="size-6" />}
      </div>
      <p className="text-sm font-medium text-text weight-display">{title}</p>
      {body ? <p className="max-w-sm text-sm text-muted">{body}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

/**
 * Errors do not apologise and they are never vague. They say what happened and what the
 * operator can do about it. The RFC 7807 `detail` is written to be shown to a user — the
 * spec says so explicitly — so we show it rather than replacing it with a shrug.
 */
export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const { t } = useI18n();

  const apiError = error instanceof ApiError ? error : null;

  let title = t("error.title");
  let detail = t("error.generic");

  if (apiError) {
    if (apiError.status === 0) {
      detail = t("error.network");
    } else if (apiError.status === 403) {
      title = t("error.forbidden");
      detail = apiError.problem.detail ?? detail;
    } else if (apiError.status === 401) {
      detail = t("error.unauthorized");
    } else if (apiError.status === 404) {
      title = t("error.notFound");
      detail = apiError.problem.detail ?? t("error.notFoundBody");
    } else {
      detail = apiError.problem.detail ?? apiError.problem.title;
    }
  }

  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center justify-center gap-3 px-6 py-12 text-center",
        className,
      )}
    >
      <AlertTriangle className="size-6 text-dry" aria-hidden />
      <p className="text-sm font-medium text-text weight-display">{title}</p>
      <p className="max-w-md text-sm text-muted">{detail}</p>
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry} className="mt-2">
          {t("common.retry")}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * The form-level error strip: what the server said about the request as a whole, when it
 * is not attributable to one field. Field-level failures go under their inputs instead —
 * see `ApiError.fieldErrors`.
 */
export function FormError({ error }: { error: unknown }) {
  const { t } = useI18n();
  if (!error) return null;

  const apiError = error instanceof ApiError ? error : null;
  const message =
    apiError?.status === 0
      ? t("error.network")
      : (apiError?.problem.detail ?? apiError?.problem.title ?? t("error.generic"));

  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-[6px] border border-dry/30 bg-dry-wash px-3 py-2 text-sm text-dry"
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
      <span>{message}</span>
    </div>
  );
}
